/**
 * Identity provider abstraction + real Cognito implementation.
 *
 * Pure TS — no React. Supplies the current user's display name and alias, which
 * the MetaInfo builder uses for actor attribution on every request.
 *
 * People API is intentionally NOT used here (deferred pending PAPI onboarding);
 * identity is sourced from the AmazonFederate (Midway) claims embedded in the
 * Cognito id token.
 */

import {
  cognitoTokenProvider,
  type CognitoTokenProvider,
} from './tokenProvider';

export interface Identity {
  /** Human-readable full name, e.g. "Jane Smith". */
  name: string;
  /** Amazon login/alias, e.g. "jsmith". */
  alias: string;
}

export interface IdentityProvider {
  getIdentity(): Promise<Identity>;
}

/** Fallback identity used before sign-in or when claims are unavailable. */
export const FALLBACK_IDENTITY: Identity = {
  name: 'Vector User',
  alias: 'unknown',
};

/**
 * Legacy stub identity provider. Retained as the documented fallback source and
 * for tests; it always resolves the configured stub identity.
 */
export class MidwayIdentityProvider implements IdentityProvider {
  private readonly stub: Identity;

  constructor(stub?: Partial<Identity>) {
    this.stub = {
      name: stub?.name ?? FALLBACK_IDENTITY.name,
      alias: stub?.alias ?? FALLBACK_IDENTITY.alias,
    };
  }

  async getIdentity(): Promise<Identity> {
    return this.stub;
  }
}

/** Minimal shape of the AmazonFederate/Cognito id-token claims we consume. */
interface IdTokenClaims {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  preferred_username?: string;
  'cognito:username'?: string;
  /** Federated identities, either an array or a JSON-encoded string. */
  identities?:
    | Array<{ userId?: string; providerName?: string }>
    | string;
}

/** base64url -> UTF-8 JSON decode of a JWT payload segment. */
function decodeJwtClaims(token: string): IdTokenClaims | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    return null;
  }
}

/** Strips a federated-IdP prefix like "AmazonFederate_jsmith" -> "jsmith". */
function stripIdpPrefix(username: string): string {
  const idx = username.indexOf('_');
  return idx >= 0 ? username.slice(idx + 1) : username;
}

/** Derives { name, alias } from id-token claims, or null if nothing usable. */
export function identityFromClaims(claims: IdTokenClaims): Identity | null {
  // alias: prefer the email local-part, then cognito:username / identities.
  let alias: string | undefined;
  if (claims.email && claims.email.includes('@')) {
    alias = claims.email.split('@')[0];
  }
  if (!alias && claims.preferred_username) {
    alias = stripIdpPrefix(claims.preferred_username);
  }
  if (!alias && claims['cognito:username']) {
    alias = stripIdpPrefix(claims['cognito:username']);
  }
  if (!alias && claims.identities) {
    const identities =
      typeof claims.identities === 'string'
        ? (JSON.parse(claims.identities) as Array<{ userId?: string }>)
        : claims.identities;
    alias = identities?.[0]?.userId;
  }

  // name: prefer the full name claim, then given+family, then the alias.
  let name = claims.name?.trim();
  if (!name && (claims.given_name || claims.family_name)) {
    name = [claims.given_name, claims.family_name]
      .filter(Boolean)
      .join(' ')
      .trim();
  }
  if (!name) name = alias;

  if (!alias && !name) return null;
  return {
    name: name ?? alias ?? FALLBACK_IDENTITY.name,
    alias: alias ?? FALLBACK_IDENTITY.alias,
  };
}

/**
 * Derives identity from the Cognito id-token (AmazonFederate/Midway claims).
 * Falls back to a stub identity when no token is present or claims can't be
 * parsed (e.g. before sign-in).
 */
export class CognitoIdentityProvider implements IdentityProvider {
  constructor(
    private readonly tokenProvider: CognitoTokenProvider = cognitoTokenProvider,
    private readonly fallback: Identity = FALLBACK_IDENTITY,
  ) {}

  async getIdentity(): Promise<Identity> {
    const token = this.tokenProvider.getRawIdentityToken();
    if (!token) return this.fallback;
    const claims = decodeJwtClaims(token);
    if (!claims) return this.fallback;
    return identityFromClaims(claims) ?? this.fallback;
  }
}

/** Default identity provider singleton used by the API facade AND the auth UI. */
export const cognitoIdentityProvider: IdentityProvider =
  new CognitoIdentityProvider();

/** Back-compat alias: the previous default export name. */
export const midwayIdentityProvider: IdentityProvider = cognitoIdentityProvider;

export default cognitoIdentityProvider;
